/**
 * 「动态」的正文。
 *
 * 标题、日期、摘要与标签放在 `site.json`（sitemap 生成脚本要读），
 * 正文放在这里。每篇都对应仓库里一个真实的设计决策或实现细节，不做营销式杜撰。
 */
import type { Block } from './blocks';

export interface NewsArticle {
  slug: string;
  blocks: readonly Block[];
}

export const NEWS_AUTHOR = 'Havorix 工程团队';

/** 从块文本粗略估算阅读时长，只用于列表与文章头的提示。 */
export function estimateMinutes(blocks: readonly Block[]): number {
  const chars = blocks.reduce((total, block) => {
    switch (block.kind) {
      case 'p':
      case 'h2':
      case 'h3':
      case 'quote':
        return total + block.text.length;
      case 'ul':
      case 'ol':
        return total + block.items.join('').length;
      case 'code':
        return total + block.code.length;
      case 'table':
        return total + block.head.join('').length + block.rows.join('').length;
    }
  }, 0);
  return Math.max(2, Math.round(chars / 420));
}

const ARTICLES: readonly NewsArticle[] = [
  {
    slug: 'single-drone-desktop-only',
    blocks: [
      {
        kind: 'p',
        text: '「单机管理」是平台中信息密度最高的一页：一张地图、一个姿态球、四项大字号指标、一份完整遥测数据列表，以及一排指令下发按钮。若将其全部压缩至手机单屏，无论如何重排，都难免相互挤占。',
      },
      {
        kind: 'p',
        text: '因此该页在移动端**不做适配**，仅提供一屏说明与一个「仍然查看」按钮。这是明确要求的取舍，并非开发进度的妥协。',
      },
      { kind: 'h2', text: '这一页服务于值守场景' },
      {
        kind: 'p',
        text: '单机管理页真正被使用的时刻，是有人坐在屏幕前持续盯守一台机体：等待起飞、观察电量下降、在告警亮起时按下指令。这类场景的物理形态本就是桌面显示器或值班大屏，因此我们额外提供了全屏模式，供监管人员整屏值守，按 `Esc` 退出。',
      },
      {
        kind: 'p',
        text: '顺带说明这些数字为何必须"大"：机体侧 MAVLink 的输出约定为 1 Hz，界面**不应**将渲染节奏绑定在帧到达频率上，否则看似更"实时"，实际只是接收了重复帧。姿态球与四项大数字通过 `ref` 直接写样式、不经过 React，仅完整数据列表采用节流快照。这项设计的收益只有在大屏上才能体现。',
      },
      { kind: 'h2', text: '为何不提供"折损版移动端"' },
      {
        kind: 'p',
        text: '我们尝试过两种看似更友好的方案，最终均予否决：',
      },
      {
        kind: 'ol',
        items: [
          '将地图与姿态球叠放并以标签页切换 —— 但最常用的动作（对照位置与姿态判断是否正常）需在两个页签间反复跳转。',
          '仅保留大数字、舍弃完整列表 —— 于是"某字段的具体数值是多少"只能依赖推测，而该页存在的意义恰恰在于提供准确值。',
        ],
      },
      {
        kind: 'p',
        text: '两种妥协都会使该页失去唯一作用，因此我们选择把限制讲清楚：移动端提供一屏说明与明确入口，进入后界面会提示小屏体验受限，您也可选择继续。假装可用，比明确不可用更为不妥。',
      },
      { kind: 'h2', text: '代价确实存在' },
      {
        kind: 'p',
        text: '巡检途中若临时需要查看某台机体的完整遥测，在手机上无法实现。我们选择将这一代价写在明面上，而不是提供一个每次都需要横屏缩放的界面来充数。',
      },
      {
        kind: 'p',
        text: '若未来移动端需求确实增强，更合理的做法是开发**专门的移动端视图**（仅回答"当前是否正常"这一个问题），而非将本页压扁。这是两件不同的事：前者是新产品，后者是坏产品。',
      },
    ],
  },
  {
    slug: 'telemetry-link-degradation',
    blocks: [
      {
        kind: 'p',
        text: '浏览器与机体之间是一条**直连**链路 —— 遥测不经过平台 Worker 中转。其收益是不必为高频数据承担带宽与算力开销，代价则是链路质量完全取决于现场网络：机房内的机体与野外作业的机体，掉线概率并不在同一量级。',
      },
      {
        kind: 'p',
        text: '因此这条链路自设计之初即按"会发生故障"来构建。以下是自建立、降级到恢复的完整过程。',
      },
      { kind: 'h2', text: '一、先检查，再连接' },
      {
        kind: 'ul',
        items: [
          '**混合内容检查**：HTTPS 页面不允许连接 `http://` / `ws://`（`localhost` 除外）。此类情况直接进入 `blocked` 状态并给出可读的原因，而非任由浏览器抛出难以理解的报错。',
          '**获取令牌**：并发请求会被合并，并在令牌到期前 15 秒提前刷新，避免推流刚建立即被 401 中断。',
          '**拉取能力声明**：先请求机体的信息接口，首屏即可呈现数据，同时验证地址与鉴权是否可用。排障时的第一步通常是"先确认这个接口是否通畅"。',
        ],
      },
      { kind: 'h2', text: '二、两条路径并行启动，以先可用者为准' },
      {
        kind: 'p',
        text: 'WebSocket 推流与 REST 轮询**并非互斥选项**，而是并行启动：轮询定时器先行运行，WebSocket `open` 之后停止轮询。如此即便推流握手稍有延迟，界面也不会先空白数秒。',
      },
      {
        kind: 'p',
        text: '两条路径产出的是同一份规范化遥测对象，上层组件无需感知当前使用哪一条 —— 但也**不能忽视其差异**：轮询模式下频率与实时性均较低，界面会将这一点表达出来。',
      },
      { kind: 'h2', text: '三、断开之后如何降级' },
      {
        kind: 'table',
        head: ['阶段', '行为'],
        rows: [
          ['WebSocket 断开', '立即回到轮询，界面持续更新，不进入"无数据"状态'],
          ['重连', '指数退避，1 秒起步，上限 15 秒，恢复后切回推流'],
          ['标签页回到前台', '立即补发一次连接 —— 后台标签页的定时器会被浏览器节流，不可依赖其自行唤醒'],
          ['超过设定时长无新帧', '判定失联，并说明原因为超时，而非凭据问题'],
        ],
      },
      {
        kind: 'p',
        text: '失联判定默认 3000 毫秒，可配置区间为 500 至 60000 毫秒。取 3000 是因为机体以 1 Hz 上报：3 秒无帧意味着已连续漏掉三帧，继续等待并无意义。',
      },
      { kind: 'h2', text: '四、宁可少给信息，也不给抖动信息' },
      {
        kind: 'p',
        text: '总览页的链路质量仅呈现分级文字（良好 / 一般 / 稍弱 / 很差），不呈现百分比。因为在 1 Hz 采样下，精确数值会持续小幅跳动，操作员盯着一个从 71% 跳成 69% 的数字，只会开始怀疑自己的判断。',
      },
      {
        kind: 'ul',
        items: [
          '**死区**：候选值落在当前档位 ±3 之内即保持不变，升降方向分别判断。',
          '**驻留**：越过死区后，需连续 2 帧成立方切换档位。',
          '**例外**：跨两档以上（例如故障注入使质量从 88 直接降至 12）立即切换，不等驻留 —— 显而易见的事实无需积累证据。',
        ],
      },
      {
        kind: 'p',
        text: '精确数值并未被隐藏：悬停提示与单机管理页的完整数据列表中均有呈现。分级仅是**总览页**的表达方式，因为该页回答的是"是否需要立即处理"，而非"具体数值是多少"。',
      },
      { kind: 'h2', text: '五、能够承认自身故障的界面，才值得信任' },
      {
        kind: 'p',
        text: '这套设计的核心只有一句话：链路降级可以接受，掩盖降级不可接受。一个在断链后继续平滑显示历史数据的界面，比一个如实变暗并写明原因的界面危险得多 —— 前者会让人在错误的前提下按下指令。',
      },
    ],
  },
  {
    slug: 'native-dialogs-are-a-bug',
    blocks: [
      {
        kind: 'p',
        text: '需求中有一条明确的约定：不允许使用浏览器系统弹窗。我们没有把它写成文档中的一句叮嘱，而是加装了一个开发期守卫 —— `window.alert` / `window.confirm` / `window.prompt` 在开发模式下被替换为会明确报错的版本。',
      },
      { kind: 'h2', text: '原生弹窗在此场景下具体的问题' },
      {
        kind: 'ul',
        items: [
          '**它会阻塞主线程**。在实时监管界面中，弹窗出现的数秒内，遥测渲染与告警可见性均被冻结，用户被迫先处理一个与飞行无关的问题。',
          '**它的呈现位置与样式不受控制**。我们投入了相当精力让确认浮层从触发的按钮旁展开、位置固定缓存、支持标题说明与危险色调，而原生弹窗会将这一切抹平为系统默认外观。',
          '**它在不同环境中表现不一致**。桌面浏览器、移动端与独立窗口模式下的样式与行为均有差异，这些差异我们无法控制，只能承担。',
          '**它无法与异步流程配合**。权限判定、指令下发与错误回显均需经过网络，而原生弹窗只能返回一个同步布尔值，信息量不足。',
        ],
      },
      { kind: 'h2', text: '为何采用"会报错的替身"而非注释说明' },
      {
        kind: 'p',
        text: '依赖自觉执行的约束，等同于没有约束。将原生 API 替换为报错的替身后，任何遗漏的调用都不会再弹出系统框，而是在控制台留下明确线索：调用的是哪个 API、参数为何、正确写法应当如何。',
      },
      {
        kind: 'code',
        lang: 'ts',
        code: "window.confirm = (...args) => {\n  report('confirm', args);\n  return false;\n};\n\n// 控制台输出：\n// [havorix] 检测到原生弹窗调用 window.confirm(\"删除该设备？\")。\n// 本平台禁止使用浏览器系统弹窗，请改用统一浮层系统：\n// import { overlay } from '@/overlay';\n// await overlay.confirm({ placementKey, trigger: event.currentTarget, title, description });",
      },
      { kind: 'h2', text: '安全默认值：明确失败，而非静默成功' },
      {
        kind: 'p',
        text: '同步 API 无法以异步浮层等价替换 —— 调用方期待的是一个立即返回的值，而浮层需等待用户点击。我们的处理方式是返回**安全默认值**：`confirm` 返回 `false`，`prompt` 返回 `null`。',
      },
      {
        kind: 'p',
        text: '于是行为变为："操作未执行 + 控制台留有清晰线索"，而非"操作被静默执行"。在会下发飞行指令的系统中，默认否定始终比默认肯定更为安全。',
      },
      {
        kind: 'p',
        text: '该守卫仅在开发模式安装（预发环境支持强制开启），生产构建中不会替换原生 API —— 到那时也不应再有未清理的调用。',
      },
    ],
  },
  {
    slug: 'control-plane-data-plane',
    blocks: [
      {
        kind: 'p',
        text: '平台与机体之间仅有两条通路：**控制面**负责账号、权限、设备档案与令牌签发；**数据面**是浏览器与机体的直接连接。这条边界一旦划错，后续所有关于成本的讨论都将失去意义。',
      },
      { kind: 'h2', text: '为何不由平台转发遥测' },
      {
        kind: 'p',
        text: '若将遥测经平台中转，收益是机体不必对浏览器可达，一台机体可同时被任意多人查看。代价则是平台需为每台机体的每一帧持续付费，且角色定位从"管得住"变为"转得多"。',
      },
      {
        kind: 'p',
        text: '我们选择前者而非后者：平台仅承载"管"所必需的低带宽请求，因此空闲时段成本接近于零，规模扩大时成本也呈线性增长。',
      },
      { kind: 'h2', text: '那么平台应当保存什么' },
      {
        kind: 'table',
        head: ['数据', '是否保存', '原因'],
        rows: [
          ['账号、角色与权限', '保存', '这是"管得住"的前提'],
          ['设备档案与接入凭据', '保存（凭据加密）', '机体地址的唯一定义处'],
          ['平台设置与告警阈值', '保存', '需跨设备、跨会话保持一致'],
          ['审计日志', '保存，默认保留 180 天', '事后查证是监管的底线'],
          ['**历史航迹**', '**不保存**', '航迹来自机体自身的实时接口，页面打开期间于内存中累积，刷新即清空'],
          ['历史遥测帧', '不保存', '平台并不持有这些数据'],
        ],
      },
      {
        kind: 'p',
        text: '最后两行是本架构最容易被误读之处。平台确实能在地图上绘制航迹，但那仅是**本次浏览会话**的累积结果 —— 关闭标签页即不复存在。我们不会在界面上暗示存在一份实际并不存在的历史数据。',
      },
      { kind: 'h2', text: '代价必须一并说明' },
      {
        kind: 'ul',
        items: [
          '机体须可被浏览器访问。仅在内部网络可达的机体，需配合边缘代理或专线路由方案。',
          'HTTPS 页面无法连接 `http://` / `ws://` 的机体，生产环境必须为机体配置 TLS。此条无绕行方案，只能提前规划。',
          '短期令牌设有有效期（默认 90 秒，可配置 15 至 3600 秒），因此客户端需提前刷新并合并并发请求，否则推流刚建立即会被 401 中断。',
          '机体与 API 分属不同子域时，需由一层轻量反向代理统一顶点域名入口，处理跨域与证书问题。',
        ],
      },
      {
        kind: 'p',
        text: '这些代价都是可预估、可提前规划的工程问题。相较之下，"为每一帧遥测承担中转成本"是一个会随规模持续放大的结构性问题。我们宁愿承担前者。',
      },
    ],
  },
  {
    slug: 'threshold-ordering',
    blocks: [
      {
        kind: 'p',
        text: '告警阈值看似只是一个填写数值的配置项，但其中存在一个极易写反之处 —— 不同物理量的"劣化方向"是相反的。',
      },
      { kind: 'h2', text: '两组方向相反的阈值' },
      {
        kind: 'p',
        text: '电量越低越危险，因此注意 > 警告 > 严重；温度越高越危险，因此注意 < 警告 < 严重。若将这两类置入同一张表单、以同一方向校验，便会写出一套方向相反的配置。',
      },
      {
        kind: 'table',
        head: ['物理量', '劣化方向', '注意', '警告', '严重'],
        rows: [
          ['电量', '越低越危险', '35%', '25%', '15%'],
          ['单体电压', '越低越危险', '3.6 V', '3.5 V', '3.4 V'],
          ['可见卫星数', '越少越危险', '10', '7', '5'],
          ['光流质量', '越低越危险', '50', '30', '15'],
          ['链路质量', '越低越危险', '60', '40', '20'],
          ['HDOP', '越高越危险', '1.5', '2.5', '4'],
          ['延迟', '越高越危险', '150 ms', '300 ms', '600 ms'],
          ['姿态偏差', '越高越危险', '25°', '35°', '45°'],
          ['电池温度', '越高越危险', '55 ℃', '65 ℃', '75 ℃'],
        ],
      },
      {
        kind: 'p',
        text: '这九组阈值即平台的默认配置。默认值整体偏保守 —— 在监管场景下，宁可提前告警，不可漏报。',
      },
      { kind: 'h2', text: '写反会导致什么' },
      {
        kind: 'p',
        text: '若某一组的三个数值方向不一致，单帧数据便可能**同时命中三个等级**。更严重的是，等级会因此失去意义：人们愿意为"严重"暂停手中事务，是因为它代表明确的紧迫程度；而一个在任何数值上都可能成立的等级，很快会被视为噪音而忽略。',
      },
      {
        kind: 'p',
        text: '因此这类问题不应依赖"填表时更加谨慎"，而应在模型层面加以区分：劣化方向是阈值定义的一部分，而非一句说明文字。',
      },
      { kind: 'h2', text: '三层覆盖，由粗至细' },
      {
        kind: 'ol',
        items: [
          '**平台默认值**：面向通用监管场景的保守取值。',
          '**平台设置覆盖**：在系统设置中按整个机队统一调整，例如现场网络普遍较差时放宽延迟要求。',
          '**单机覆盖**：某台机体工况特殊时单独设置，不影响机队其余成员。',
        ],
      },
      {
        kind: 'p',
        text: '合并规则十分明确：自默认值出发，逐层以存在的覆盖项叠加；非法值（非数字、非有限数、无法识别的键名）直接忽略，而非令整份配置失效。如此，一次填写错误最多导致某个覆盖项未生效，不会使整个机队的告警功能失效。',
      },
    ],
  },
  {
    slug: 'mock-fleet-first',
    blocks: [
      {
        kind: 'p',
        text: '平台开发中有一项工作排序十分靠前：先构建一支**并不存在的机队**。在接入任何真机之前，6 台模拟机体已在本地运行。',
      },
      { kind: 'h2', text: '它的形态' },
      {
        kind: 'ul',
        items: [
          '6 台机体，各自监听独立端口（9201 至 9206），互不干扰。',
          '一个注册表接口，返回这 6 台机体的端点字段与适配配置 —— 在「无人机管理」页执行一次批量导入，整支机队即完成建档。',
          '每台机体一份独立的接入凭据，便于验证凭据隔离是否生效。',
          '一个故障注入接口，可按单台或整队注入异常。',
        ],
      },
      { kind: 'h2', text: '故障注入才是其真正的价值所在' },
      {
        kind: 'p',
        text: '一个只会返回"一切正常"的模拟服务器几乎没有用处，因为真实世界的问题集中在异常路径上。因此模拟机队可按要求进入异常状态：',
      },
      {
        kind: 'table',
        head: ['模式', '行为', '用于验证'],
        rows: [
          ['offline', '所有接口返回 503，并断开 WebSocket', '断链判定与失联状态'],
          ['slow', 'REST 延迟指定毫秒数', '超时边界与降级路径'],
          ['flaky', '随机丢包、随机断流', '重连退避与恢复逻辑'],
          ['unauthorized', '凭据失效（探活接口除外）', '凭据轮换与排障提示'],
        ],
      },
      {
        kind: 'p',
        text: '`unauthorized` 特意保留了探活接口可用 —— 现实中排障的第一步始终是"这台设备是否仍在线"，若连探活都失败，后续便无从查起。这一约束随后被直接写入接入文档：凭据失效或鉴权异常时，探活接口必须仍然可用。',
      },
      { kind: 'h2', text: '这份文档是双向的' },
      {
        kind: 'p',
        text: '模拟机队不仅是开发工具，它同时是**那份接口文档的可执行版本**。针对它的测试会将接入协议的每一条真实运行一遍：真正启动 HTTP 与 WebSocket 服务，真正发送请求，真正断开连接。',
      },
      {
        kind: 'p',
        text: '因此这份文档是可信的：任何机体只要满足它，平台侧无需改动一行代码。反过来说，若平台需要修改文档，测试会先行拦截。',
      },
      { kind: 'h2', text: '附带用途：演示与教学' },
      {
        kind: 'p',
        text: '同一支模拟机队也部署至线上环境，使任何人无需接入真机即可完整操作一遍流程 —— 注册、总览、单机管理、指令下发、告警与降级。这项能力后来成为教学与算法验证场景中使用频率最高的部分：失败可复现，且成本为零。',
      },
      {
        kind: 'p',
        text: '先构建一支不存在的机队，看似比直接接入真机更为迂回。但正因如此，整条链路才得以在没有任何硬件的情况下被完整验证与回归。',
      },
    ],
  },
];

const ARTICLE_MAP: ReadonlyMap<string, NewsArticle> = new Map(
  ARTICLES.map((article) => [article.slug, article]),
);

export const NEWS_SLUGS: readonly string[] = ARTICLES.map((article) => article.slug);

export function newsArticle(slug: string): NewsArticle | undefined {
  return ARTICLE_MAP.get(slug);
}

/** 相邻文章。`order` 是按日期倒序的 slug 列表，因此 index-1 更新、index+1 更早。 */
export function newsNeighbours(
  slug: string,
  order: readonly string[],
): { newer?: string; older?: string } {
  const index = order.indexOf(slug);
  if (index < 0) return {};
  return {
    newer: index > 0 ? order[index - 1] : undefined,
    older: index < order.length - 1 ? order[index + 1] : undefined,
  };
}
